import { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../utils/fatch';
import styles from './passwordReset.module.css';

/**
 * Step one of a password reset: ask for the email address.
 * The server answers the same way whether or not the account exists, so this
 * page always shows the same confirmation.
 */
export function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (sending || !email.trim()) return;

        setSending(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Could not send the reset email');
                return;
            }

            setSent(true);
        } catch (err) {
            console.error('Forgot password error:', err);
            setError('Could not reach the server. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                <div className={styles.brand}>RoamRite</div>

                {sent ? (
                    <>
                        <div className={styles.icon}>📬</div>
                        <h1>Check your inbox</h1>
                        <p className={styles.sub}>
                            If <strong>{email}</strong> has an account, a reset link is on its way. It works for the
                            next hour and can only be used once.
                        </p>
                        <p className={styles.hint}>
                            Nothing arrived? Check spam, or{' '}
                            <button type="button" className={styles.linkBtn} onClick={() => setSent(false)}>
                                try another address
                            </button>.
                        </p>
                        <Link to="/login" className={styles.ghostBtn}>Back to sign in</Link>
                    </>
                ) : (
                    <>
                        <div className={styles.icon}>🔑</div>
                        <h1>Forgot your password?</h1>
                        <p className={styles.sub}>
                            Enter the email you signed up with and we&apos;ll send you a link to choose a new password.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <label className={styles.label} htmlFor="reset-email">Email</label>
                            <input
                                id="reset-email"
                                type="email"
                                className={styles.input}
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={sending}
                                required
                                autoComplete="email"
                            />

                            {error && <p className={styles.error}>{error}</p>}

                            <button type="submit" className={styles.primaryBtn} disabled={sending || !email.trim()}>
                                {sending ? 'Sending…' : 'Send reset link'}
                            </button>
                        </form>

                        <Link to="/login" className={styles.ghostBtn}>Back to sign in</Link>
                    </>
                )}
            </div>
        </div>
    );
}
